import { useState, useEffect, useCallback, useRef } from "react";
import { useToast } from "components-lib";
import { axiosInstance, API_ENDPOINTS } from "config/api";
import { cachedApiCall, CACHE_KEYS } from "utils/apiCache";

// Global cache for recommendations to share across all hook instances
const globalRecommendationsCache = new Map();
const globalFetchPromises = new Map();

/**
 * Custom hook for fetching and managing doctor recommendations
 * Fetches suggestions for medicines, diagnosis, complaints, allergies, and advice
 * from the API endpoint and provides them for autocomplete functionality
 * 
 * OPTIMIZED: Uses global caching and request deduplication to prevent
 * multiple API calls when used across different components.
 */
export const useDoctorRecommendations = (specificDoctorId = null) => {
    const toast = useToast();
    const hasFetchedRef = useRef(false);
    const currentDoctorIdRef = useRef(null);

    // State for doctor recommendations
    const [recommendations, setRecommendations] = useState(() => {
        // Initialize from global cache if available
        const doctorId = specificDoctorId || JSON.parse(localStorage.getItem("tokenPayload") || "{}")?.userId;
        if (doctorId && globalRecommendationsCache.has(doctorId)) {
            return globalRecommendationsCache.get(doctorId);
        }
        // Try localStorage as fallback
        try {
            const stored = localStorage.getItem("doctorRecommendations");
            if (stored) return JSON.parse(stored);
        } catch (e) { /* ignore */ }

        return {
            medicines: [],
            diagnosis: [],
            complaints: [],
            allergies: {
                medicines: [],
                food: [],
                other: [],
            },
            advices: [],
        };
    });

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Process API response into structured recommendations
     */
    const processApiResponse = useCallback((responseData) => {
        if (!responseData) return null;

        return {
            medicines:
                responseData.medicines
                    ?.map((medicine) => medicine.name)
                    .filter(Boolean) || [],
            medicinesFreq:
                responseData.medicines
                    ?.map((medicine) => medicine.frequency)
                    .filter(Boolean) || [],
            diagnosis:
                responseData.diagnosis
                    ?.map((diag) => diag.diagnosis)
                    .filter(Boolean) || [],
            complaints:
                responseData.complaints?.filter(
                    (complaint) => complaint.complaint,
                ) || [],
            allergies: {
                medicines:
                    responseData.allergies
                        ?.map((allergy) => allergy.medicines)
                        .filter(Boolean) || [],
                food:
                    responseData.allergies
                        ?.map((allergy) => allergy.food)
                        .filter(Boolean) || [],
                other:
                    responseData.allergies
                        ?.map((allergy) => allergy.others)
                        .filter(Boolean) || [],
            },
            advices:
                responseData.advices
                    ?.map((advice) => ({
                        id: advice.id,
                        text: advice.advice_text
                    }))
                    .filter((a) => a.text) || [],
        };
    }, []);

    /**
     * Fetch doctor recommendations from API with caching
     * Uses the provided doctor ID or falls back to current user's ID from localStorage
     */
    const fetchRecommendations = useCallback(async (force = false) => {
        // Resolve doctor ID
        let doctorId = specificDoctorId;
        if (!doctorId) {
            const tokenPayload = JSON.parse(
                localStorage.getItem("tokenPayload") || "{}",
            );
            doctorId = tokenPayload?.userId;
        }

        if (!doctorId) {
            console.warn(
                "[useDoctorRecommendations] No doctor ID found",
            );
            return;
        }

        // Check global cache first (unless force refresh)
        if (!force && globalRecommendationsCache.has(doctorId)) {
            console.log(`[useDoctorRecommendations] Using cached data for doctor ${doctorId}`);
            const cached = globalRecommendationsCache.get(doctorId);
            setRecommendations(cached);
            return cached;
        }

        // Check if there's already a fetch in progress for this doctor
        if (globalFetchPromises.has(doctorId)) {
            console.log(`[useDoctorRecommendations] Waiting for in-flight request for doctor ${doctorId}`);
            try {
                const result = await globalFetchPromises.get(doctorId);
                setRecommendations(result);
                return result;
            } catch (e) {
                // If in-flight request failed, continue to make a new request
            }
        }

        setIsLoading(true);
        setError(null);

        const cacheKey = CACHE_KEYS.DOCTOR_RECOMMENDATIONS(doctorId);

        // Create the fetch promise
        const fetchPromise = cachedApiCall(
            cacheKey,
            async () => {
                console.log(`[useDoctorRecommendations] Fetching from API for doctor ${doctorId}`);
                const response = await axiosInstance.get(
                    `${API_ENDPOINTS.DOCTOR.GET_RECOMMENDATIONS}?doctor_id=${doctorId}`,
                );
                return response.data;
            },
            { ttl: 5 * 60 * 1000, force } // 5 minute TTL
        ).then((data) => {
            const processedRecommendations = processApiResponse(data);

            if (processedRecommendations) {
                // Update global cache
                globalRecommendationsCache.set(doctorId, processedRecommendations);

                // Store in localStorage for offline access
                localStorage.setItem(
                    "doctorRecommendations",
                    JSON.stringify(processedRecommendations),
                );
            }

            return processedRecommendations;
        });

        // Register in-flight promise
        globalFetchPromises.set(doctorId, fetchPromise);

        try {
            const result = await fetchPromise;
            if (result) {
                setRecommendations(result);
            }
            return result;
        } catch (err) {
            console.error("[useDoctorRecommendations] Error fetching:", err);
            setError("Failed to load recommendations");

            // Try to load from localStorage as fallback
            try {
                const stored = localStorage.getItem("doctorRecommendations");
                if (stored) {
                    const parsed = JSON.parse(stored);
                    setRecommendations(parsed);
                    console.log("[useDoctorRecommendations] Loaded from localStorage fallback");
                }
            } catch (storageError) {
                console.error("[useDoctorRecommendations] localStorage fallback failed:", storageError);
            }

            toast({
                title: "Warning",
                description: "Could not load latest recommendations. Using cached data.",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
            globalFetchPromises.delete(doctorId);
        }
    }, [specificDoctorId, processApiResponse, toast]);

    /**
     * Get unique severity levels from complaints data
     */
    const getSeverityOptions = useCallback(() => {
        const severities = recommendations.complaints
            .map((complaint) => complaint.severity)
            .filter(Boolean)
            .filter((severity, index, arr) => arr.indexOf(severity) === index);

        const defaultSeverities = ["Mild", "Moderate", "Severe", "Acute", "Chronic"];
        return [...new Set([...severities, ...defaultSeverities])];
    }, [recommendations.complaints]);

    /**
     * Get complaint suggestions (just the complaint text)
     */
    const getComplaintSuggestions = useCallback(() => {
        return recommendations.complaints
            .map((complaint) => complaint.complaint)
            .filter(Boolean)
            .filter((complaint, index, arr) => arr.indexOf(complaint) === index);
    }, [recommendations.complaints]);

    /**
     * Get severity for a specific complaint
     */
    const getSeverityForComplaint = useCallback(
        (complaintText) => {
            const complaint = recommendations.complaints.find(
                (c) =>
                    c.complaint &&
                    c.complaint.toLowerCase() === complaintText.toLowerCase(),
            );
            return complaint?.severity || "";
        },
        [recommendations.complaints],
    );

    // Fetch recommendations on mount (only once per doctor ID)
    useEffect(() => {
        const doctorId = specificDoctorId || JSON.parse(localStorage.getItem("tokenPayload") || "{}")?.userId;

        // Only fetch if we haven't fetched for this doctor ID yet
        if (doctorId && (doctorId !== currentDoctorIdRef.current || !hasFetchedRef.current)) {
            currentDoctorIdRef.current = doctorId;
            hasFetchedRef.current = true;
            fetchRecommendations();
        }

        // No interval - we rely on cache TTL and manual refresh when needed
    }, [specificDoctorId, fetchRecommendations]);

    return {
        recommendations,
        isLoading,
        error,
        fetchRecommendations: () => fetchRecommendations(true), // Force refresh when called manually
        getSeverityOptions,
        getComplaintSuggestions,
        getSeverityForComplaint,
    };
};
